/* eslint-disable eslint-comments/no-unlimited-disable */
/* global firebase */
/* global galite */
import config from '#/config/firebase';
import { screenViewAnalyticsEvent } from '#/config/constants';
import { isTizen, isVIZIO } from '#/utils/componentStyleConfig';

const isClient = typeof window !== 'undefined';

const defaultAnalyticsParams = {
  device_type: isVIZIO() ? 'vizio' : isTizen() ? 'tizen' : 'web',
  device_model: isClient ? window.navigator.userAgent : '',
};

let defaultUserProperties = {};

const api = {
  // Add default properties so user properties are guaranteed to be reset before analytics calls
  updateDefaultUserProperties(properties) {
    defaultUserProperties = {
      ...defaultUserProperties,
      ...properties,
    };
  },
};
const bufferedScreenEvents = [];
const bufferedEvents = [];

const initFirebaseAnalytics = () => {
  const firebaseAnalyticsScript = document.createElement('script');
  firebaseAnalyticsScript.src = config.FIREBASE_ANALYTICS_SCRIPT;
  firebaseAnalyticsScript.onload = () => {
    const { logEvent } = firebase.analytics();
    api.sendAnalytics = (evt, payload = {}) =>
      logEvent(evt, {
        ...payload,
        ...defaultAnalyticsParams,
      });

    api.setUserProperties = firebase.analytics().setUserProperties;
    api.setUserId = firebase.analytics().setUserId;
    api.setCurrentScreen = firebase.analytics().setCurrentScreen;

    api.sendScreenViewAnalytics = screenName => {
      api.setCurrentScreen(screenName);
      api.setUserProperties(defaultUserProperties);
      api.sendAnalytics(screenViewAnalyticsEvent, {
        screen_name: screenName,
      });
    };

    bufferedEvents.forEach(event =>
      api.sendAnalytics(event.evt, event.payload),
    );
    bufferedScreenEvents.forEach(api.sendScreenViewAnalytics);
  };
  document.head.appendChild(firebaseAnalyticsScript);
};

const getMappedEventArgs = payload => {
  // NOTE: There are limited properties that galite will send
  // Complete list: https://github.com/jehna/ga-lite/blob/master/src/fields-to-params.js

  // Data map object
  // custom metrics and dimensions are used
  const {
    device_type: dimension1 = null,
    device_model: dimension2 = null,
    instance_id: dimension3 = null,
    group_id: dimension4 = null,
    video_id: dimension5 = null,
    video_player_control_type: dimension6 = null,
    video_dimensions: dimension7 = null,
    video_initiation_method: dimension8 = null,
    video_player_type: dimension9 = null,
    video_streaming_type: dimension10 = null,
    screen_name: dimension11 = null,
    unique_key: dimension12 = null,
    user_id: dimension13 = null, // Kid's profile id
    age_group: dimension14 = null,
    parent_id: dimension15 = null,
    registered = null,
    time_to_initialize: metric2 = null,
    video_duration: metric3 = null,
    video_current_time: metric4 = null,
    video_percent_complete: metric5 = null,
    video_bitrate: metric6 = null,
  } = {
    ...defaultUserProperties,
    ...defaultAnalyticsParams,
    ...payload,
  };

  // Change bool to number
  const metric1 = registered === null ? null : Number(registered);

  return {
    dimension1,
    dimension2,
    dimension3,
    dimension4,
    dimension5,
    dimension6,
    dimension7,
    dimension8,
    dimension9,
    dimension10,
    dimension11,
    dimension12,
    dimension13,
    dimension14,
    dimension15,
    metric1,
    metric2,
    metric3,
    metric4,
    metric5,
    metric6,
    userId: dimension15,
  };
};

const initGA3Analytics = () => {
  /* eslint-disable */
  (function(e, t, n, i, s, a, c) {
    e[n] =
      e[n] ||
      function() {
        (e[n].q = e[n].q || []).push(arguments);
      };
    a = t.createElement(i);
    c = t.getElementsByTagName(i)[0];
    a.async = true;
    a.src = s;
    c.parentNode.insertBefore(a, c);
  })(
    window,
    document,
    'galite',
    'script',
    'https://cdn.jsdelivr.net/npm/ga-lite@2/dist/ga-lite.min.js',
  );
  /* eslint-enable */

  galite('create', config.trackingIdTizen, 'auto');

  // Calls to galite are queued, it will run
  // until the tracker above is done being created
  galite(() => {
    api.sendAnalytics = (evt, payload = {}) => {
      const args = getMappedEventArgs(payload);

      // WARNING: action and hitType must be positional arguments
      // action, hitType, eventCategory, eventAction, payload
      galite('send', 'event', 'Samsung', evt, args);
    };

    api.setUserProperties = props => {
      galite('set', props);
    };
    api.setUserId = user_id => {
      // NOTE: user_id (kid) on tizen is mapped to
      // dimension13 (cd13) and parent_id is mapped to
      // userId (uid) and dimension15 (cd15)
      // for concistency with Roku.
      // Firebase analytics sets user_id as userId
      api.updateDefaultUserProperties({ user_id });
    };
    api.setCurrentScreen = screenName => {
      galite('set', { screenName });
    };

    api.sendScreenViewAnalytics = screenName => {
      api.setCurrentScreen(screenName);
      api.setUserProperties(defaultUserProperties);
      api.sendAnalytics(screenViewAnalyticsEvent, {
        screen_name: screenName,
      });
    };

    // Delay queued events
    setTimeout(() => {
      bufferedEvents.forEach(event =>
        api.sendAnalytics(event.evt, event.payload),
      );
      bufferedScreenEvents.forEach(api.sendScreenViewAnalytics);
    }, 1000);
  });
};

// Make sure that the firestore script is ready for the getAvatars call that happens very
// early during initialization on AppShell.
const fireStoreLoadingProm = new Promise(resolve => {
  // If client browser, load firestore scripts, avoid during webpack compilation

  if (isClient) {
    const firebaseAppScript = document.createElement('script');
    firebaseAppScript.src = config.FIREBASE_APP_SCRIPT;
    firebaseAppScript.onload = () => {
      const apiKey = isVIZIO()
        ? config.apiKeyVizio || config.apiKey
        : isTizen()
        ? config.apiKeyTizen || config.apiKey
        : config.apiKey;
      const appId = isVIZIO()
        ? config.appIdVizio || config.appId
        : isTizen()
        ? config.appIdTizen || config.appId
        : config.appId;
      const measurementId = isVIZIO()
        ? config.measurementIdVizio || config.measurementId
        : isTizen()
        ? config.measurementIdTizen || config.measurementId
        : config.measurementId;

      // Init after script load
      firebase.initializeApp({
        apiKey,
        authDomain: config.authDomain,
        projectId: config.projectId,
        storageBucket: config.storageBucket,
        messagingSenderId: config.messagingSenderId,
        appId,
        measurementId,
      });

      const firebaseInstallationScript = document.createElement('script');
      firebaseInstallationScript.src = config.FIREBASE_INSTALLATIONS_SCRIPT;
      firebaseInstallationScript.onload = async () => {
        const installationId = await firebase.installations().getId();
        defaultAnalyticsParams.instance_id = installationId;

        // load analytics and firestore script
        const firestoreScript = document.createElement('script');
        firestoreScript.onload = () => {
          resolve();
        };
        firestoreScript.src = config.FIREBASE_STORE_SCRIPT;

        // Firestore analytics do not work on Samsung
        // Use galite (GA3) instead
        // Current implementation relies on 5 functions of api
        //  - api.sendAnalytics
        //  - api.setUserProperties
        //  - api.setUserId
        //  - api.setCurrentScreen
        //  - api.sendScreenViewAnalytics
        if (isTizen()) {
          initGA3Analytics();
        } else {
          initFirebaseAnalytics();
        }

        document.head.appendChild(firestoreScript);
      };

      document.head.appendChild(firebaseInstallationScript);
    };

    document.head.appendChild(firebaseAppScript);
  } else {
    resolve();
  }
});

api.sendAnalytics = (evt, payload = {}) =>
  bufferedEvents.push({ evt, payload });
api.sendScreenViewAnalytics = screenName =>
  bufferedScreenEvents.push(screenName);

api.getAllByCollection = async (collectionName, orderBy, age) => {
  const firestore = firebase?.firestore();
  const response = orderBy
    ? age
      ? await firestore
          .collection(collectionName)
          .where('ages', 'array-contains', age)
          .orderBy(orderBy, 'asc')
          .where('hide', '==', false)
          .get()
      : await firestore
          .collection(collectionName)
          .orderBy(orderBy, 'asc')
          .where('hide', '==', false)
          .get()
    : await firestore
        .collection(collectionName)
        .where('hide', '==', false)
        .get();

  const currentSeconds = Date.now() / 1000;
  return response.docs.reduce((result, doc) => {
    const data = doc.data();
    if (!data.start_date || !data.end_date) {
      result.push(data);
      return result;
    }

    if (
      data.start_date.seconds < currentSeconds &&
      data.end_date.seconds > currentSeconds
    ) {
      result.push(data);
    }
    return result;
  }, []);
};

api.getAvailableTopics = async age => {
  const firestore = firebase?.firestore();
  const query = firestore
    .collection('topics')
    .where('hide', '==', false)
    .where('ages', 'array-contains', age)
    .orderBy('name', 'asc');
  const response = await query.get();
  return response.docs.map(doc => doc.data());
};

api.getTopic = async id => {
  const firestore = firebase?.firestore();
  const query = firestore
    .collection('topics')
    .where('hide', '==', false)
    .where('id', '==', id);
  const response = await query.get();
  return response.docs.map(doc => doc.data());
};

api.getAvailableShows = async age => {
  const firestore = firebase?.firestore();
  const query = firestore
    .collection('shows')
    .where('hide', '==', false)
    .where('ages', 'array-contains', age)
    .orderBy('name', 'asc');
  const response = await query.get();
  const currentSeconds = Date.now() / 1000;
  return response.docs.reduce((result, doc) => {
    const data = doc.data();
    if (
      data.start_date.seconds < currentSeconds &&
      data.end_date.seconds > currentSeconds
    ) {
      result.push(data);
    }
    return result;
  }, []);
};

api.getContentById = async (contentId, collectionName) => {
  const firestore = firebase?.firestore();
  const response = await firestore
    .collection(collectionName)
    .where('id', '==', contentId)
    .get();
  return response.docs.map(doc => doc.data());
};

api.getAvatars = async () => {
  await fireStoreLoadingProm;
  const firestore = firebase?.firestore();
  const response = await firestore.collection('avatars').get();
  return response.docs.map(doc => doc.data());
};

export default api;
